import { dividerHorizontal } from '@filament/react/atomic-styles';
import { Button } from '@filament/react/button';
import { Card, CardBody, CardHeader } from '@filament/react/card';
import { Cross } from '@filament/react/icons/cross';
import React, { type ReactNode } from 'react';
import * as styles from './DetailsPanel.css';

type DetailsPanelProps = {
    onClose: () => void;
    children: ReactNode;
    as?: 'form' | 'div';
    onSubmit?: (e: React.SubmitEvent) => void;
}

const DetailsPanelRoot = ({ onClose, children, as = 'div', onSubmit }: DetailsPanelProps) => {
    const childArray = React.Children.toArray(children);

    const head = childArray.find(child => React.isValidElement(child) && child.type === DetailsPanelHead);
    const content = childArray.find(child => React.isValidElement(child) && child.type === DetailsPanelContent);
    const footer = childArray.find(child => React.isValidElement(child) && child.type === DetailsPanelFooter);

    const card = (
        <Card className={styles.container}>
            <CardHeader>
                <Button
                    className={styles.closeButton}
                    variant="quiet"
                    shape="square"
                    isFullWidth={false}
                    isIconOnly
                    aria-label="Close details panel"
                    onPress={onClose}
                >
                    <Cross />
                </Button>
            </CardHeader>

            {head}
            {content}
            {footer}
        </Card>
    );

    if (as === 'form') {
        return (
            <form onSubmit={onSubmit} className={styles.container}>
                {card}
            </form>
        );
    }

    return <div className={styles.container}>{card}</div>;
};

const DetailsPanelHead = ({ children }: { children: ReactNode }) => (
    <CardBody className={styles.fixed}>{children}</CardBody>
);

const DetailsPanelContent = ({ children }: { children: ReactNode }) => (
    <CardBody className={styles.scrollable}>{children}</CardBody>
);

const DetailsPanelFooter = ({ children }: { children: ReactNode }) => (
    <>
        <CardBody className={styles.fixed}>
            <hr className={dividerHorizontal} />
        </CardBody>
        {children}
    </>
);

export const DetailsPanel = Object.assign(DetailsPanelRoot, {
    Head: DetailsPanelHead,
    Content: DetailsPanelContent,
    Footer: DetailsPanelFooter,
});
